import { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Legal.module.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles["legal-page"]}>
      <div className={styles["legal-container"]}>
        <Link to="/" className={styles["back-link"]}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5"></path>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Home
        </Link>

        <header className={styles["legal-header"]}>
          <h1>Privacy Policy</h1>
          <p className={styles["legal-date"]}>Last updated: May 12, 2026</p>
        </header>

        <div className={styles["legal-content"]}>
          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to WebTwin Platform ("we," "our," or "us"). We are committed to protecting your
              personal information and your right to privacy. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our platform, including our
              website, 3D immersive experiences, and content management system.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us when you:</p>
            <ul>
              <li>Register for an account</li>
              <li>Log in to the platform</li>
              <li>Contact us through forms</li>
              <li>Use our interactive features</li>
            </ul>
            <p>This information may include:</p>
            <ul>
              <li>Name and email address</li>
              <li>Account credentials</li>
              <li>Contact information</li>
              <li>Messages and feedback</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>
              When you access our platform, we may automatically collect certain information, including:
            </p>
            <ul>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>IP address</li>
              <li>Access times and dates</li>
              <li>Pages viewed and interactions with 3D scenes</li>
              <li>Device information and screen resolution</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, operate, and maintain our platform</li>
              <li>Deliver immersive 3D and XR experiences</li>
              <li>Manage user accounts and authentication</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Improve and optimize our platform performance</li>
              <li>Send administrative notifications</li>
              <li>Monitor usage patterns and analytics</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Sharing and Disclosure</h2>
            <p>
              We do not sell, trade, or otherwise transfer your personal information to outside parties
              except in the following circumstances:
            </p>
            <ul>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>With trusted service providers who assist us in operating our platform, subject to
                confidentiality agreements</li>
              <li>In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your
              personal information against unauthorized access, alteration, disclosure, or destruction.
              These measures include encryption, access controls, and regular security audits.
            </p>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure.
              While we strive to use commercially acceptable means to protect your personal information,
              we cannot guarantee its absolute security.
            </p>
          </section>

          <section>
            <h2>6. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience on our platform.
              Cookies are small data files stored on your device that help us:
            </p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Authenticate your sessions</li>
              <li>Analyze platform usage</li>
              <li>Optimize 3D scene loading and performance</li>
            </ul>
            <p>
              You can control cookie preferences through your browser settings. Disabling cookies may
              affect certain features of the platform.
            </p>
          </section>

          <section>
            <h2>7. Third-Party Services</h2>
            <p>
              Our platform may integrate with third-party services such as Unity WebGL, analytics
              providers, and content delivery networks. These services have their own privacy policies,
              and we encourage you to review them. We are not responsible for the privacy practices of
              third-party services.
            </p>
          </section>

          <section>
            <h2>8. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes
              outlined in this Privacy Policy, unless a longer retention period is required or permitted
              by law. When your data is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2>9. Your Rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your data in a structured format</li>
              <li><strong>Objection:</strong> Object to certain processing of your data</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our platform is not intended for children under the age of 13. We do not knowingly collect
              personal information from children under 13. If we become aware that we have collected such
              information, we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or
              applicable laws. We will notify you of any material changes by posting the updated policy on
              our platform with a revised "Last Updated" date.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <div className={styles["contact-box"]}>
              <p><strong>WebTwin Platform</strong></p>
              <p>Email: 
                <a href="mailto:developer@saecons.com"> developer@saecons.com</a>
                </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
